import React from 'react';
import { themed } from '../../ui/theme';

export type ActionKind = 'approve' | 'reject' | 'return' | 'neutral';

const ICONS: Record<ActionKind, React.ReactNode> = {
  approve: <path d="M20 6L9 17l-5-5" />,
  reject: <path d="M18 6L6 18M6 6l12 12" />,
  return: <path d="M9 14L4 9l5-5M4 9h11a5 5 0 0 1 0 10h-1" />,
  neutral: <path d="M5 12h14" />,
};

// Approve is the "happy path" → solid. Reject/Return keep their semantic colour
// but render as outline so they don't compete with the primary action.
const SOLID: Record<ActionKind, string> = {
  approve: 'border-emerald-600 bg-emerald-600 text-white hover:bg-emerald-700',
  reject: 'border-red-600 bg-red-600 text-white hover:bg-red-700',
  return: 'border-amber-500 bg-amber-500 text-white hover:bg-amber-600',
  neutral: 'border-[#1351B4] bg-[#1351B4] text-white hover:bg-[#0f3f8c]',
};

const OUTLINE: Record<ActionKind, string> = {
  approve: 'border-emerald-600 bg-white text-emerald-700 hover:bg-emerald-50 dark:bg-transparent dark:text-emerald-400 dark:hover:bg-emerald-900/20',
  reject: 'border-red-600 bg-white text-red-700 hover:bg-red-50 dark:bg-transparent dark:text-red-400 dark:hover:bg-red-900/20',
  return: 'border-amber-500 bg-white text-amber-700 hover:bg-amber-50 dark:bg-transparent dark:text-amber-400 dark:hover:bg-amber-900/20',
  neutral: 'border-slate-300 bg-white text-slate-600 hover:bg-slate-50 dark:border-slate-600 dark:bg-transparent dark:text-slate-300 dark:hover:bg-slate-700/40',
};

interface Props {
  kind: ActionKind;
  onClick: () => void;
  children: React.ReactNode;
  variant?: 'solid' | 'outline';
  size?: 'sm' | 'md';
  loading?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  className?: string;
}

const Spinner: React.FC = () => (
  <svg aria-hidden="true" viewBox="0 0 24 24" className="h-3.5 w-3.5 animate-spin" fill="none">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
    <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
  </svg>
);

const ActionButton: React.FC<Props> = ({
  kind, onClick, children, variant = 'solid', size = 'sm', loading = false, disabled = false, fullWidth = false, className = '',
}) => {
  const tone = variant === 'outline' ? OUTLINE[kind] : SOLID[kind];
  const sizeCls = size === 'md' ? 'px-4 py-2 text-sm' : 'px-3 py-1.5 text-xs';
  const iconCls = size === 'md' ? 'h-4 w-4' : 'h-3.5 w-3.5';
  const radius = themed({ minimal: 'rounded-md', saas: 'rounded-lg' });
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || loading}
      aria-busy={loading}
      className={`inline-flex items-center justify-center gap-1.5 border font-medium transition disabled:opacity-40 ${radius} ${sizeCls} ${tone} ${fullWidth ? 'w-full' : ''} ${className}`}
    >
      {loading ? <Spinner /> : (
        <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" className={iconCls}>
          {ICONS[kind]}
        </svg>
      )}
      {children}
    </button>
  );
};

export default ActionButton;
